import { Icon } from '@/components/ui/Icon'
import { resolveImageUrl } from '@/lib/image-url'
import { processImageAsFile } from '@/lib/image'
import { fileService } from '@services'
import { useRef, useState } from 'react'
import styles from './ImageUpload.module.scss'

interface UploadedImage {
  id: string
  url: string
  isUploading?: boolean
  error?: string
  localPreview?: string
}

interface ImageUploadProps {
  images: UploadedImage[]
  onImagesChange: (images: UploadedImage[]) => void
  maxImages?: number
  className?: string
  testId?: string
}

export const ImageUpload = ({
  images,
  onImagesChange,
  maxImages = 10,
  className = '',
  testId = 'image-upload',
}: ImageUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const imagesRef = useRef(images)
  const [uploadingCount, setUploadingCount] = useState(0)

  // Keep ref in sync with prop (synchronously during render)
  imagesRef.current = images

  const handleUploadClick = () => {
    if (images.length >= maxImages) return
    fileInputRef.current?.click()
  }

  const updateImage = (
    tempId: string,
    update: Partial<UploadedImage> | null
  ) => {
    const currentImages = imagesRef.current
    let newImages: UploadedImage[]
    if (update === null) {
      // Remove the image
      newImages = currentImages.filter((img) => img.id !== tempId)
    } else {
      // Update the image
      newImages = currentImages.map((img) =>
        img.id === tempId ? { ...img, ...update } : img
      )
    }
    imagesRef.current = newImages
    onImagesChange(newImages)
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const remainingSlots = maxImages - images.length
    const filesToUpload = Array.from(files).slice(0, remainingSlots)

    // Create placeholders for all files first
    const newImages: UploadedImage[] = []
    const uploadTasks: { file: File; tempId: string; previewUrl: string }[] = []

    for (const file of filesToUpload) {
      if (!file.type.startsWith('image/')) continue

      const tempId = `temp-${Date.now()}-${Math.random()}`
      const previewUrl = URL.createObjectURL(file)

      newImages.push({
        id: tempId,
        url: previewUrl,
        localPreview: previewUrl,
        isUploading: true,
      })

      uploadTasks.push({ file, tempId, previewUrl })
    }

    // Add all placeholders at once
    const updatedImages = [...images, ...newImages]
    imagesRef.current = updatedImages
    onImagesChange(updatedImages)
    setUploadingCount(uploadTasks.length)

    // Upload files in parallel
    await Promise.all(
      uploadTasks.map(async ({ file, tempId, previewUrl }) => {
        try {
          // Process image: resize to max 500px, convert to JPG, compress to 50%
          const processedFile = await processImageAsFile(file)

          // Upload using fileService
          const response = await fileService.upload(processedFile)

          // Update with server response - use URL as id since that's what the server returns
          updateImage(tempId, {
            id: response.url,
            url: response.url,
            isUploading: false,
            localPreview: undefined,
          })

          // Clean up blob URL
          URL.revokeObjectURL(previewUrl)
        } catch (error) {
          console.error('Failed to upload image:', error)
          updateImage(tempId, {
            isUploading: false,
            error: 'Upload failed',
          })
        } finally {
          setUploadingCount((prev) => prev - 1)
        }
      })
    )

    // Reset input so the same file can be selected again
    e.target.value = ''
  }

  const handleRemoveImage = (id: string) => {
    const imageToRemove = images.find((img) => img.id === id)
    if (imageToRemove?.localPreview) {
      URL.revokeObjectURL(imageToRemove.localPreview)
    }
    onImagesChange(images.filter((img) => img.id !== id))
  }

  const handleRetry = (id: string) => {
    // Remove the failed image - user can re-upload
    handleRemoveImage(id)
  }

  return (
    <div className={`${styles.container} ${className}`} data-testid={testId}>
      <div className={styles.header}>
        <h3 className={styles.title}>Photos</h3>
        <button
          type="button"
          className={styles.uploadButton}
          onClick={handleUploadClick}
          disabled={uploadingCount > 0 || images.length >= maxImages}
          data-testid={`${testId}-button`}
        >
          {uploadingCount > 0 ? 'Uploading...' : 'Upload'}
          <Icon name="plus" size={16} className={styles.uploadIcon} />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileChange}
          style={{ display: 'none' }}
          data-testid={`${testId}-input`}
        />
      </div>

      {images.length > 0 && (
        <div className={styles.grid} data-testid={`${testId}-grid`}>
          {images.map((image, index) => (
            <div
              key={image.id}
              className={styles.imageItem}
              data-testid={`${testId}-item-${index}`}
            >
              <img
                src={image.localPreview || resolveImageUrl(image.url)}
                alt="Uploaded"
                className={`${styles.image} ${image.isUploading ? styles.imageLoading : ''} ${image.error ? styles.imageError : ''}`}
              />

              {image.isUploading && (
                <div className={styles.overlay}>
                  <Icon name="spinner" size={24} className={styles.spinner} />
                </div>
              )}

              {image.error && (
                <div className={styles.errorOverlay}>
                  <span className={styles.errorText}>{image.error}</span>
                  <button
                    type="button"
                    className={styles.retryButton}
                    onClick={() => handleRetry(image.id)}
                    data-testid={`${testId}-retry-${image.id}`}
                  >
                    Retry
                  </button>
                </div>
              )}

              {!image.isUploading && !image.error && (
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={() => handleRemoveImage(image.id)}
                  data-testid={`${testId}-remove-${image.id}`}
                >
                  <Icon
                    name="x"
                    size={12}
                    color="#ffffff"
                    className={styles.removeIcon}
                  />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export type { UploadedImage }
